import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import HeadingCp from '../components/HeadingCp';
import ShortDateFormat from '../components/ShortDateFormat';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const TechOpQueue = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ops, setOps] = useState([]);

  useEffect(() => {
    setLoading(true);
    const fetchQueue = async () => {
      try {
        const { data } = await axios.get(`${apiUrl}/operation_crud.php?getOpTechList=1`, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
        });
        if (Array.isArray(data) && data[0] === 'success') {
          setOps(Array.isArray(data[1]) ? data[1] : []);
        } else if (data?.success && Array.isArray(data.data)) {
          setOps(data.data);
        } else {
          toast.error(data?.error || 'Failed to load queue', { position: 'top-center' });
        }
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/login');
        } else if (err.response?.status === 403) {
          toast.error("You don't have access to this area.", { position: 'top-center' });
        } else {
          toast.error('Error loading queue', { position: 'top-center' });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchQueue();
  }, [apiUrl, navigate]);

  const handleActivation = async (id, status) => {
    // If already active/in progress, jump to details
    if (status === 'In Progress' || status === 'Scheduled') {
      navigate(`/t/op/${id}`);
      return;
    }
    // Otherwise try to activate
    try {
      const body = { status: 'opActivate', data: { id } };
      const { data } = await axios.post(`${apiUrl}/operation_crud.php`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      const ok = (Array.isArray(data) && data[0] === 'success') || data?.success;
      if (ok) {
        navigate(`/t/op/${id}`);
      } else {
        toast.info(data?.error || 'Failed to access this job.', { position: 'top-center' });
      }
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else if (err.response?.status === 403) {
        toast.error("You don't have access to this area.", { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <HeadingCp label="Operation Queue" />
      <ul role="list" className="divide-y divide-gray-100">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {ops.map(op => (
            <div
              key={op.id}
              onClick={() => handleActivation(op.id, op.status)}
              className={classNames(
                `relative flex items-center space-x-3 rounded-lg border border-gray-300 bg-white
                 px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500
                 focus-within:ring-offset-2 hover:border-gray-400`
              )}
            >
              <div className="flex-shrink-0 text-xs">
                <div className="text-sm font-semibold text-indigo-700">#{op.id}</div>
                <div className="mt-1">
                  <span className="rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white shadow-sm">
                    {op.status || 'Scheduled'}
                  </span>
                </div>
                <p className="py-1">{op.start_time ? op.start_time : ''}</p>
                <p>
                  {op.schedule_from
                    ? <ShortDateFormat date={op.schedule_from} />
                    : op.schedule_from}
                </p>
              </div>

              <div className="min-w-0 flex-1">
                <span aria-hidden="true" className="absolute inset-0" />
                <p className="text-sm font-medium text-gray-700">
                  {op.description || '-'}
                </p>
                <p className="text-sm font-medium text-green-700">
                  Tech: {op.tech_name || '-'}
                </p>
                {/* If you later join projects/customers, show them here */}
                <p className="truncate text-sm text-gray-500">
                  Logged: {op.date} {op.time_log}
                </p>
                {op.user_comment && (
                  <p className=" text-xs text-yellow-700">
                    Coordinator: {op.user_comment}
                  </p>
                )}
              </div>
            </div>
          ))}
          {!loading && ops.length === 0 && (
            <div className="text-gray-500 px-6 py-4">No operation schedules found.</div>
          )}
        </div>
      </ul>
    </>
  );
};

export default TechOpQueue;
